import React, {
  useState,
  FC,
  MutableRefObject,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { motion, useDragControls } from "framer-motion";
import { HiChevronDown as CollapsedIcon } from "react-icons/hi";
import { v4 as uuid } from "uuid";

import styles from "./styles.module.scss";
import { ROTATIONS } from "./constants";

type Props = {
  header: React.ReactElement;
  className?: string;
  headerClassName?: string;
  disabled?: boolean;
  withIcon?: boolean;
  collapseDirection?: "up" | "down";
  drag?: boolean | "x";
  dragConstraints?: MutableRefObject<HTMLElement | null>;
  unmountOnCollapse?: boolean;
  withBackdrop?: boolean;
  height?: number | string;
  open?: boolean;
  onSelect?(id: string): void;
  id?: string;
  contentClassName?: string;
};

const Collapsable: FC<Props> = ({
  contentClassName,
  header,
  children,
  className,
  headerClassName,
  disabled,
  withIcon,
  collapseDirection = "down",
  drag = false,
  dragConstraints,
  unmountOnCollapse = false,
  withBackdrop = false,
  height,
  open,
  id,
  onSelect,
}) => {
  const componentId = useMemo(() => id ?? uuid(), []);
  const [collapsed, setCollapsed] = useState(true);
  const dragControls = useDragControls();

  const [unmountChildren, setUnmountChildren] = useState(true);

  useEffect(() => {
    if (!unmountChildren) setCollapsed(false);
  }, [unmountChildren]);

  const documentClickHandler = useCallback((event) => {
    if (!event.target.closest(`#collapsable-${componentId}`))
      setCollapsed(true);
  }, []);

  useEffect(() => {
    if (withBackdrop) document.addEventListener("click", documentClickHandler);
    return () => document.removeEventListener("click", documentClickHandler);
  }, []);

  return (
    <motion.div
      id={`collapsable-${componentId}`}
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={dragConstraints}
      drag={drag}
      dragMomentum={false}
      className={`${styles.container} ${className}`}
    >
      {drag && (
        <div
          onPointerDown={(event) => dragControls.start(event)}
          className={styles.handle}
        />
      )}
      <div
        className={`${styles.header} ${headerClassName}`}
        aria-hidden
        onClick={
          disabled
            ? () => {}
            : () => {
                if (onSelect) onSelect(componentId);
                setCollapsed(!collapsed);
                if (collapsed) setUnmountChildren(false);
              }
        }
      >
        {header}
        {withIcon && (
          <CollapsedIcon
            className={styles.icon}
            style={{
              transition: "all 0.2s ease-in-out",
              transform: (open === undefined ? collapsed : !open)
                ? ROTATIONS[collapseDirection].initial
                : ROTATIONS[collapseDirection].final,
            }}
          />
        )}
      </div>
      <motion.div
        initial={false}
        onAnimationComplete={() => {
          if (collapsed) setUnmountChildren(true);
        }}
        animate={
          (open === undefined ? collapsed : !open)
            ? { height: 0, margin: 0 }
            : {
                height: height ?? "fit-content",
                transition: { delay: 0.05 },
                margin: "15px 0 25px",
              }
        }
        className={`${styles.children} ${contentClassName}`}
      >
        {(unmountOnCollapse ? !unmountChildren : true) && children}
      </motion.div>
    </motion.div>
  );
};

Collapsable.defaultProps = {
  className: "",
  headerClassName: "",
  disabled: false,
  withIcon: false,
  collapseDirection: "down",
  drag: false,
  dragConstraints: undefined,
  unmountOnCollapse: false,
  withBackdrop: false,
  height: undefined,
  open: undefined,
  onSelect: undefined,
  id: undefined,
  contentClassName: undefined,
};

export default Collapsable;
